import {
    ReactNode,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const AnalyticsInfo = ({
    children,
    label,
}: {
    children: ReactNode;
    label: string;
}) => {
    const { t } = useTranslation();
    const id = useId();
    const button = useRef<HTMLButtonElement>(null);
    const tooltip = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>();
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ left: 0, top: 0 });
    const show = () => {
        clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const leave = () => {
        if (document.activeElement !== button.current)
            closeTimer.current = setTimeout(() => setOpen(false), 150);
    };
    useEffect(() => () => clearTimeout(closeTimer.current), []);
    useLayoutEffect(() => {
        if (!open || !button.current || !tooltip.current) return;
        const anchor = button.current.getBoundingClientRect();
        const box = tooltip.current.getBoundingClientRect();
        setPosition({
            left: Math.max(
                12,
                Math.min(anchor.left, window.innerWidth - box.width - 12),
            ),
            top: Math.max(
                12,
                anchor.bottom + box.height + 20 < window.innerHeight
                    ? anchor.bottom + 8
                    : anchor.top - box.height - 8,
            ),
        });
    }, [open, children]);
    useEffect(() => {
        if (!open) return;
        const outside = (event: PointerEvent) => {
            if (
                !button.current?.contains(event.target as Node) &&
                !tooltip.current?.contains(event.target as Node)
            )
                setOpen(false);
        };
        const escape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        const close = (event: Event) => {
            if (
                event.target instanceof Node &&
                tooltip.current?.contains(event.target)
            )
                return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', outside);
        document.addEventListener('keydown', escape);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('pointerdown', outside);
            document.removeEventListener('keydown', escape);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);
    return (
        <>
            <button
                ref={button}
                type="button"
                className="analytics-info"
                aria-label={t('admin.analytics.infoFor', { title: label })}
                aria-describedby={open ? id : undefined}
                onMouseEnter={show}
                onMouseLeave={leave}
                onFocus={show}
                onBlur={() => setOpen(false)}
                onClick={show}
            >
                <Info size={17} strokeWidth={1.8} aria-hidden="true" />
            </button>
            {open &&
                createPortal(
                    <div
                        ref={tooltip}
                        id={id}
                        role="tooltip"
                        className="analytics-info-tooltip"
                        style={position}
                        onMouseEnter={show}
                        onMouseLeave={leave}
                    >
                        {children}
                    </div>,
                    document.body,
                )}
        </>
    );
};
